/**
 * RC8 Phase 1 · Slice 6 · Offline Foundation · Platform Adapter.
 *
 * PO-ratified 2026-02-17. Owns the ONE decision about which persistence
 * engine the mobile offline foundation talks to at runtime.
 *
 * The rule (per ADR-MOBILE-003 §4):
 *   · Capacitor native (iOS / Android)  → `@capacitor-community/sqlite`
 *                                           (real SQLite; SQLCipher for C2/C3)
 *   · Web preview                       → in-memory + `localStorage` fallback
 *                                           for C1 / C2 only. C3 FAILS CLOSED.
 *
 * This module never invents encryption. If the native platform does not
 * expose a verified encryption path, C2 opens with `encryption_available =
 * false` and C3 refuses to persist entirely. The alternative (silent
 * base64/obfuscation) is explicitly banned by ADR-MOBILE-003 §"Rejected".
 *
 * No desktop surface consumes this module. Web preview only touches it
 * via the mobile-first `MobileToday` / `MobileVoyage` surfaces (Slice 3-4).
 */
import { Capacitor } from "@capacitor/core";

/**
 * Runtime capability probe. Returns the truth about the environment we
 * are running in — never speculative, never optimistic.
 */
export function detectPlatform() {
  // `Capacitor.isNativePlatform()` is only true inside a Capacitor
  // native shell (iOS / Android). In the web preview it's false.
  const isNative =
    typeof Capacitor !== "undefined" &&
    typeof Capacitor.isNativePlatform === "function" &&
    Capacitor.isNativePlatform();
  const platform =
    typeof Capacitor !== "undefined" &&
    typeof Capacitor.getPlatform === "function"
      ? Capacitor.getPlatform()
      : "web";

  return {
    is_native: !!isNative,
    platform,                            // "ios" | "android" | "web"
    // C2 encryption: the Capacitor native SQLite plugin ships SQLCipher
    // support. On web preview the plaintext localStorage/IndexedDB path
    // does NOT satisfy the ADR-MOBILE-003 §3.2.C2 encryption contract.
    c2_encryption_available: !!isNative,
    // C3 protection: the biometric-gated encrypted store requires BOTH
    // native SQLCipher AND platform biometrics. We do NOT enable C3 in
    // web preview. This is a fail-closed guarantee.
    c3_protection_available: !!isNative,
    // The web preview persists via `localStorage` for C1 + C2 as a
    // preview-only convenience. Production C2 will never see this path.
    preview_persistence: !isNative ? "localStorage" : null,
  };
}

/**
 * Convenience singleton — one detection per module load. Consumers may
 * import `PLATFORM` directly rather than calling `detectPlatform()`.
 */
export const PLATFORM = detectPlatform();
